/* eslint-disable @typescript-eslint/no-explicit-any */
// Typed doorway to the legacy JS hooks. Types live in ./types.ts; nothing else
// in src/app|features|shared imports these hooks directly.
import * as configLogic from 'hooks/useConfigurationLogic';
import * as vendorHook from 'hooks/useVendor';
import * as priceEditions from 'hooks/usePriceEditions';
import * as execPricing from 'hooks/useExecPricing';
import * as execMetrics from 'hooks/useExecMetrics';
import * as displayMetrics from 'hooks/useDisplayMetrics';
import * as permissions from 'hooks/useUserPermissions';
import * as priceBook from 'pricing/priceBookFromEdition';
import * as resolver from 'pricing/priceResolver';
import type { ConfigLogic, CustomPrices, ExecMetricsApi, ExecPricingApi, Metrics, MetricOverrideMap, Permissions, PriceEditionsApi, Vendor, VendorApi, AppUser } from 'shared/state/types';
import type { ThroughputBasis } from 'metrics/types';

export const useConfigurationLogic = (configLogic as any).useConfigurationLogic as
  (productDb: any, metricsDb: any, vendor: Vendor, options?: { customPrices?: CustomPrices }) => ConfigLogic;

export const useVendor = (vendorHook as any).useVendor as
  (options: { userGroups: string[]; isCheckingAccess: boolean; initialVendor?: string }) => VendorApi;

export const usePriceEditions = (priceEditions as any).usePriceEditions as (vendorId: string) => PriceEditionsApi;
export const shouldPromptForNewEdition = (priceEditions as any).shouldPromptForNewEdition as
  (a: { touched: boolean; previous: number | null; next: number | null }) => boolean;

export const useExecPricing = (execPricing as any).useExecPricing as (a: {
  enabled: boolean; vendor: string; quarter?: string; asOf?: string; onOverridesReset: (reason: string) => void;
}) => ExecPricingApi;
export const useSavedPricePin = (execPricing as any).useSavedPricePin as
  (exec: ExecPricingApi, setCustomPrices: (p: CustomPrices) => void) => { pinSavedPrices: (p: CustomPrices) => void; releasePin: () => void };

// CONF-212/CONF-258: exec metric overrides, applied AFTER pricing/BOM (spec §3).
// `throughputBasis` drives both staleness and the one-time legacy-quote
// conversion; `onLegacyConverted` fires once per conversion/drop (the hook
// wraps it in a ref, so an inline arrow here is fine).
export const useExecMetrics = (execMetrics as any).useExecMetrics as
  (a: { enabledSets: { afa: boolean; hybrid: boolean }; throughputBasis?: ThroughputBasis; onLegacyConverted?: (message: string) => void }) => ExecMetricsApi;
export const useDisplayMetrics = (displayMetrics as any).useDisplayMetrics as (
  metrics: Metrics | null, overrides: MetricOverrideMap, opts: { isPBUnit: boolean },
) => { displayMetrics: Metrics | null; affectedKeys: Set<string>; staleKeys: Set<string> };

export const useUserPermissions = (permissions as any).useUserPermissions as (user: AppUser | null) => Permissions;

export const priceBookFromEdition = (priceBook as any).priceBookFromEdition as (edition: unknown, productDb: any) => any;
export const createPriceResolver = (resolver as any).createPriceResolver as (book: any, quarter: string, overrides: CustomPrices) => any;
